// src/features/stream/streamModel.ts
/**
 * .tuMetrics {"tu1": 0.88, "tu2": 0.9, "tu3": 8, "notes": "Unread @mentions stack as separate blocks; assigned discussion cards stay on all when read", "tuVersion": "2.5"}
 * .help "Turns raw feed events into the stream's blocks: resolves which entity an event is about, applies the active lens, groups events per entity (unread @mentions stay separate until read), and finds quiet items that need finishing."
 */
// Currents 2.0 — the stream is a derivation over data the stores already hold.
// No DB objects (decision 2026-09-29). Everything here is a pure function.

import type { Card, FeedEvent, Project, Sprint, User } from '../../types';
import type { Lens } from './lens';
import { feedStreamRecipientId } from '../feed/twoHref.ts';
import { isWorkCardType } from '../../utils/cardTypeUtils.ts';

export type EntityRefType =
  | 'card' | 'sprint' | 'roadmap' | 'plan' | 'feature' | 'project'
  | 'stream' | 'guide' | 'none';

export interface EntityRef {
  type: EntityRefType;
  id: string;
}

export interface StreamGroup {
  key: string;
  ref: EntityRef;
  events: FeedEvent[];      // newest first
  latestAt: string;         // ISO
}

/** Which entity is this event about? Cards win because they are the finest grain. */
export function refForEvent(event: FeedEvent): EntityRef {
  const guideId = event.metadata?.guide_id as string | undefined;
  if (
    guideId &&
    (event.type === 'guide_published' ||
      event.type === 'guide_draft_updated' ||
      event.type === 'guide_author_added')
  ) {
    return { type: 'guide', id: guideId };
  }
  const metaPlanId = event.metadata?.plan_id as string | undefined;
  if (
    (event.type === 'plan_draft_updated' || event.type === 'plan_author_added') &&
    (metaPlanId || event.planId)
  ) {
    return { type: 'plan', id: metaPlanId || event.planId! };
  }
  if (event.cardId) return { type: 'card', id: event.cardId };
  if (event.sprintId) return { type: 'sprint', id: event.sprintId };
  if (event.planId) return { type: 'plan', id: event.planId };
  if (event.requirementId) return { type: 'feature', id: event.requirementId };
  if (event.roadmapId) return { type: 'roadmap', id: event.roadmapId };
  if (event.projectId) return { type: 'project', id: event.projectId };
  if (event.streamType && event.recipientStreamId) {
    const partner = feedStreamRecipientId(event) || event.recipientStreamId;
    return { type: 'stream', id: `${event.streamType}:${partner}` };
  }
  return { type: 'none', id: event.id };
}

export interface LensContext {
  currentUser: User | null;
  cardsById: Map<string, Card>;
  sprintsById: Map<string, Sprint>;
  projectsById: Map<string, Project>;
}

function projectIdForEvent(event: FeedEvent, ctx: LensContext): string | null {
  if (event.projectId) return event.projectId;
  if (event.cardId) {
    const card = ctx.cardsById.get(event.cardId);
    if (card?.projectId) return card.projectId;
    if (card?.sprintId) {
      const sprint = ctx.sprintsById.get(card.sprintId);
      if (sprint?.projectId) return sprint.projectId;
    }
  }
  if (event.sprintId) {
    const sprint = ctx.sprintsById.get(event.sprintId);
    if (sprint?.projectId) return sprint.projectId;
  }
  return null;
}

function isMine(event: FeedEvent, ctx: LensContext): boolean {
  const me = ctx.currentUser?.id;
  if (!me) return false;
  if (event.initiatorId === me || event.userId === me) return true;
  if (event.cardId) {
    const card = ctx.cardsById.get(event.cardId);
    if (card?.assigneeId === me || card?.createdById === me) return true;
  }
  return false;
}

function isAbout(event: FeedEvent, personId: string, ctx: LensContext): boolean {
  if (event.initiatorId === personId || event.userId === personId) return true;
  if (event.cardId) {
    const card = ctx.cardsById.get(event.cardId);
    if (card?.assigneeId === personId) return true;
  }
  if (event.streamType === 'direct_message') {
    const partner = feedStreamRecipientId(event);
    if (partner === personId) return true;
  }
  return false;
}

function hasTag(event: FeedEvent, tag: string, ctx: LensContext): boolean {
  const wanted = tag.toLowerCase();
  const metaTags: unknown = event.metadata?.tags;
  if (Array.isArray(metaTags) && metaTags.some((t) => String(t).toLowerCase() === wanted)) return true;
  if (event.cardId) {
    const card = ctx.cardsById.get(event.cardId);
    if (card?.tags?.some((t) => t.toLowerCase() === wanted)) return true;
  }
  if (event.description?.toLowerCase().includes(`#${wanted}`)) return true;
  return false;
}

/** Apply the active lens to raw events. `quiet` is not an event lens; see quietCards(). */
export function filterEventsByLens(events: FeedEvent[], lens: Lens, ctx: LensContext): FeedEvent[] {
  switch (lens.kind) {
    case 'all': return events;
    // Time lens uses DayWorkList (work product by focused day), not the chat river.
    case 'today': return [];
    case 'mine': return events.filter((e) => isMine(e, ctx));
    case 'quiet': return [];
    case 'project': return events.filter((e) => projectIdForEvent(e, ctx) === lens.id);
    case 'person': return events.filter((e) => isAbout(e, lens.id, ctx));
    case 'tag': return events.filter((e) => hasTag(e, lens.tag, ctx));
    case 'sprint': return events.filter((e) => e.sprintId === lens.id || (
      !!e.cardId && ctx.cardsById.get(e.cardId)?.sprintId === lens.id
    ));
    case 'roadmap': return events.filter((e) => e.roadmapId === lens.id);
    case 'plan': return events.filter((e) => e.planId === lens.id);
    case 'room':
    case 'service':
    case 'guides':
    case 'shares':
    case 'inventory': {
      // These lenses render their own pane, not feed_events — keep the river empty here.
      return [];
    }
  }
}

const MENTION_TYPES = new Set([
  'stream_message_mentioned',
  'comment_mentioned',
  'mention',
]);

/** Comment-shaped activity keeps a card on `all` even after the assignee has read it. */
const DISCUSSION_EVENT_TYPES = new Set([
  'comment_added',
  'comment_replied',
  'comment_mentioned',
]);

export function groupHasDiscussion(
  events: Array<Pick<FeedEvent, 'type'>>,
): boolean {
  return events.some((e) => DISCUSSION_EVENT_TYPES.has(e.type));
}

/**
 * On the `all` lens, open board cards drop once read — unless they carry
 * discussion. Hiding a thread mid-conversation feels like the card vanished.
 */
export function keepAssignedCardOnAllLens(opts: {
  isAssignee: boolean;
  archived: boolean;
  done: boolean;
  hasUnreadForMe: boolean;
  hasDiscussion: boolean;
}): boolean {
  if (!opts.isAssignee || opts.archived || opts.done) return true;
  if (opts.hasUnreadForMe) return true;
  return opts.hasDiscussion;
}

/** Unread @mentions stay their own card until read — they must not overwrite each other. */
export function isUnreadMentionFor(
  event: Pick<FeedEvent, 'type' | 'userId' | 'read'>,
  userId: string,
): boolean {
  if (!userId || event.userId !== userId) return false;
  if (event.read?.[userId]) return false;
  return MENTION_TYPES.has(event.type);
}

/**
 * Group consecutive events about the same entity into one block. Newest first.
 * `windowMs` bounds how far apart two events can be and still share a block.
 * Pass `forUserId` so that user's unread @mentions each get their own block.
 */
export function groupEvents(
  events: FeedEvent[],
  windowMs = 6 * 60 * 60 * 1000,
  forUserId?: string,
): StreamGroup[] {
  const sorted = [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const groups: StreamGroup[] = [];
  const openByRef = new Map<string, StreamGroup>();

  for (const event of sorted) {
    const ref = refForEvent(event);
    // Stack unread mentions as separate notifications; after read they rejoin the room.
    const refKey =
      forUserId && isUnreadMentionFor(event, forUserId)
        ? `mention:${event.id}`
        : `${ref.type}:${ref.id}`;
    const at = new Date(event.createdAt).getTime();
    const open = openByRef.get(refKey);

    if (open && ref.type !== 'none' && !refKey.startsWith('mention:')) {
      const last = open.events[open.events.length - 1];
      // Rooms are one conversation: keep a single block even when events
      // are days apart. Cards/features still split after windowMs so a
      // later burst can surface near NOW without dragging last month along.
      const sameRoom = ref.type === 'stream';
      if (sameRoom || new Date(last.createdAt).getTime() - at <= windowMs) {
        open.events.push(event);
        continue;
      }
    }

    const group: StreamGroup = {
      key: `${refKey}:${event.id}`,
      ref,
      events: [event],
      latestAt: event.createdAt,
    };
    groups.push(group);
    openByRef.set(refKey, group);
  }

  return groups;
}

/** Unread counters must match visible blocks: one group, one count. */
export function unreadGroupCount(events: FeedEvent[], userId: string): number {
  if (!userId) return 0;
  return groupEvents(events, 6 * 60 * 60 * 1000, userId).filter((group) =>
    group.events.some((e) => e.userId === userId && !e.read?.[userId])
  ).length;
}

// ---------------------------------------------------------------------------
// Sediment (§6b): things of mine that stopped moving. No rows, no counts;
// one lens, sorted smallest first.
// ---------------------------------------------------------------------------

export const QUIET_DAYS_DEFAULT = 7;

export interface QuietItem {
  card: Card;
  quietSince: string;   // ISO of last movement
  daysQuiet: number;
  size: number;         // smaller = easier to finish
}

function lastMovementOf(card: Card, latestEventByCard: Map<string, string>): string {
  const candidates: string[] = [card.createdAt];
  if (card.lastActivity?.timestamp) candidates.push(card.lastActivity.timestamp);
  const fromFeed = latestEventByCard.get(card.id);
  if (fromFeed) candidates.push(fromFeed);
  return candidates.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
}

function sizeOf(card: Card): number {
  if (typeof card.complexity === 'number') return card.complexity;
  if (typeof card.timeEstimateDays === 'number') return card.timeEstimateDays * 2;
  const total = card.subtaskStats?.total ?? 0;
  const done = card.subtaskStats?.done ?? 0;
  if (total > 0) return Math.max(1, total - done);
  return 3; // unknown → middle
}

export function quietCards(
  cards: Card[],
  events: FeedEvent[],
  me: string | undefined,
  scopeProjectId: string | null = null,
  days = QUIET_DAYS_DEFAULT
): QuietItem[] {
  if (!me) return [];
  const latestEventByCard = new Map<string, string>();
  for (const e of events) {
    if (!e.cardId) continue;
    const prev = latestEventByCard.get(e.cardId);
    if (!prev || new Date(e.createdAt) > new Date(prev)) latestEventByCard.set(e.cardId, e.createdAt);
  }

  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const items: QuietItem[] = [];
  for (const card of cards) {
    if (!isWorkCardType(card.type)) continue;
    if (card.status === 'done' || card.status === 'archived') continue;
    // In-progress already lives in NOW — don't double-list under quiet.
    if (card.status === 'inProgress') continue;
    if (card.archivedAt || card.is_draft) continue;
    if (card.assigneeId !== me && card.createdById !== me) continue;
    if (scopeProjectId && card.projectId !== scopeProjectId) continue;
    const last = lastMovementOf(card, latestEventByCard);
    const lastMs = new Date(last).getTime();
    if (lastMs > cutoff) continue;
    items.push({
      card,
      quietSince: last,
      daysQuiet: Math.floor((Date.now() - lastMs) / (24 * 60 * 60 * 1000)),
      size: sizeOf(card),
    });
  }
  // Longest quiet first; size is a badge on the block, not the sort key.
  return items.sort((a, b) => b.daysQuiet - a.daysQuiet || a.size - b.size);
}
